import { mat2d, type ReadonlyVec2 } from 'gl-matrix';

import { regionIntersectsLine } from '../spatial/region-search';

import {
  boxArea,
  boxCreate,
  boxGetBoundsOfRotatedBox,
  boxIntersectionBounds,
  boxLines,
  type ReadonlyBox,
} from './box';
import { boxToRegion } from './convert';
import { boxToMat2dRotatedNormalized } from './transform';

const UNIT_BOX = boxCreate(0, 1, 0, 1);

/**
 * Tests if a bounding box intersects with another bounding box
 * @param boxA - Box
 * @param boxB - Box
 * @returns boolean
 */
export function boxIntersectsBox(boxA: ReadonlyBox, boxB: ReadonlyBox): boolean {
  return (
    boxA.right >= boxB.left &&
    boxA.left <= boxB.right &&
    boxA.bottom >= boxB.top &&
    boxA.top <= boxB.bottom
  );
}

export function boxIntersectsRotatedBox(
  unRotatedBox: ReadonlyBox,
  rotatedBox: ReadonlyBox,
  rotation: number,
  rotationOrigin?: ReadonlyVec2,
): boolean {
  // If the un-rotated box contains the rotated bounds, then they intersect
  const rotatedBounds = boxGetBoundsOfRotatedBox(rotatedBox, rotation, rotationOrigin);
  if (boxContainsBox(unRotatedBox, rotatedBounds)) {
    return true;
  }

  // If the rotated box is not entirely contained within the un-rotated box,
  // then they will intersect if any of the box lines intersect
  //
  // 1) Create a transform for the rotated coordinate space
  // 2) Get the lines of the un-rotated box in the rotated coordinate space
  // 3) Check if any of the un-rotated box lines intersect the rotated box

  const transform = boxToMat2dRotatedNormalized(rotatedBox, rotation, rotationOrigin);
  mat2d.invert(transform, transform);

  for (const [start, end] of boxLines(unRotatedBox, transform)) {
    if (boxIntersectsLine(UNIT_BOX, start, end)) {
      return true;
    }
  }

  return false;
}

/**
 * Tests if a bounding box is fully contained within another bounding box
 * @param boxA - The bigger box
 * @param boxB - The smaller box to check is contained in the bigger box
 * @returns boolean
 */
export function boxContainsBox(boxA: ReadonlyBox, boxB: ReadonlyBox): boolean {
  return (
    boxB.right <= boxA.right &&
    boxB.left >= boxA.left &&
    boxB.top >= boxA.top &&
    boxB.bottom <= boxA.bottom
  );
}

/**
 * Checks whether a child box intersects with a parent box at a given percentage (between 0 and 1 inclusive)
 * @param parentBox - The bigger box
 * @param childBox - The smaller box to check if it overlaps with the parent box with a given percentage
 * @param percent - Between 0 and 1 inclusive
 */
export function boxIntersectsBoxWithPercent(
  parentBox: ReadonlyBox,
  childBox: ReadonlyBox,
  percent: number,
): boolean {
  // It is invalid to have a percentage outside of this range
  if (percent < 0 || percent > 1) {
    return false;
  }

  // Check if the boxes overlap at all
  if (!boxIntersectsBox(parentBox, childBox)) {
    return false;
  }

  // If they do, figure out how much of the parent box is overlapped by the child box
  const parentArea = boxArea(parentBox);
  const overlapArea = boxArea(boxIntersectionBounds(parentBox, childBox));

  // If the parent has no area, it is invalid to try to check its overlap with a child box
  if (parentArea <= 0) {
    return false;
  }

  const overlapRatio = overlapArea / parentArea;
  return overlapRatio >= percent;
}

/**
 * Tests if a bounding box is horizontally contained within another bounding box
 * @param boxA - The bigger box
 * @param boxB - The smaller box to check is contained in the bigger box
 * @returns boolean
 */
export function boxHorizontallyContainsBox(
  boxA: ReadonlyBox,
  boxB: ReadonlyBox,
): boolean {
  return boxB.right <= boxA.right && boxB.left >= boxA.left;
}

/**
 * Tests if a bounding box is vertically contained within another bounding box
 * Accounts for an offset at the bottom of the bigger box
 * @param boxA - The bigger box
 * @param boxB - The smaller box to check is contained in the bigger box
 * @returns boolean
 */
export function boxVerticallyContainsBox(boxA: ReadonlyBox, boxB: ReadonlyBox): boolean {
  return boxB.top >= boxA.top && boxB.bottom <= boxA.bottom;
}

/**
 * Tests if a bounding box intersects with a line
 * @param box - ReadonlyBox
 * @param lineStart - ReadonlyVec2
 * @param lineEnd - ReadonlyVec2
 * @returns boolean
 */
export function boxIntersectsLine(
  box: ReadonlyBox,
  lineStart: ReadonlyVec2,
  lineEnd: ReadonlyVec2,
): boolean {
  const region = boxToRegion(box);
  return regionIntersectsLine(region, lineStart, lineEnd);
}

/**
 * Tests if a bounding box intersects with a point
 * @param box - Box
 * @param point - vec2
 * @returns boolean
 */
export function boxIntersectsPoint(box: ReadonlyBox, point: ReadonlyVec2): boolean {
  return (
    box.left <= point[0] &&
    point[0] <= box.right &&
    box.top <= point[1] &&
    point[1] <= box.bottom
  );
}

/**
 * Tests if a point is inside a box, not on the edge of the box
 * @param box - Box
 * @param point - vec2
 * @returns boolean
 */
export function pointInsideBox(
  box: ReadonlyBox,
  point: ReadonlyVec2,
  epsilon = 0.001,
): boolean {
  return (
    box.left + epsilon < point[0] &&
    point[0] < box.right - epsilon &&
    box.top + epsilon < point[1] &&
    point[1] < box.bottom - epsilon
  );
}
